import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  Send, 
  MessageSquare, 
  DollarSign, 
  Settings, 
  Users, 
  History, 
  Plus, 
  Check, 
  AlertCircle,
  Loader2,
  Trash2
} from 'lucide-react';
import { useToastContext } from '../context/ToastContext';
import { useEvent } from '../context/EventContext';
import Layout from '../components/layout/Layout';
import RecipientSelector from '../components/common/RecipientSelector';
import Loading from '../components/common/Loading';
import ConfirmModal from '../components/common/ConfirmModal';
import { eventAPI, attendeeAPI } from '../api/endpoints';
import api from '../api/axios';
import { getApiUrl } from '../utils/apiConfig';

const SmsCampaigns = () => {
  const toast = useToastContext();
  const { selectedEvent } = useEvent();
  
  // Loading states
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isSavingSender, setIsSavingSender] = useState(false);
  const [isInitiatingPayment, setIsInitiatingPayment] = useState(false);

  // Settings & balance
  const [balance, setBalance] = useState(0);
  const [senderId, setSenderId] = useState('InfoSMS');
  const [tempSenderId, setTempSenderId] = useState('InfoSMS');
  const [organizationName, setOrganizationName] = useState('');

  // Form states
  const [message, setMessage] = useState('');
  const [recipientType, setRecipientType] = useState('individual');
  const [selectedRecipientIds, setSelectedRecipientIds] = useState([]);
  const [personalise, setPersonalise] = useState(true);

  // Lists & data
  const [attendees, setAttendees] = useState([]);
  const [packages, setPackages] = useState({});
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [sendLogs, setSendLogs] = useState([]);
  const [selectedLogs, setSelectedLogs] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('compose'); // compose, history, payments

  // Real-time counter states
  const [charCount, setCharCount] = useState(0);
  const [segmentCount, setSegmentCount] = useState(0);
  const [encoding, setEncoding] = useState('gsm7');

  // Send status report
  const [sendResult, setSendResult] = useState(null);

  // Confirm modal for sending
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [pendingSendCount, setPendingSendCount] = useState(0);
  const [pendingSendCost, setPendingSendCost] = useState(0);
  const [isTruncated, setIsTruncated] = useState(false);

  const textareaRef = useRef(null);

  // Load Paystack library dynamically
  const loadPaystack = () => {
    return new Promise((resolve) => {
      if ((window as any).PaystackPop) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://js.paystack.co/v1/inline.js';
      script.onload = () => resolve(true);
      document.body.appendChild(script);
    });
  };

  // Character segment calculation matching PHP logic
  const calculateSmsParts = useCallback((text) => {
    if (!text) {
      return { count: 0, segments: 0, enc: 'gsm7' };
    }

    const gsm7Basic = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
    const gsm7Extension = "^{}\\[~]|€";
    
    let isUnicode = false;
    let weight = 0;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      if (gsm7Extension.includes(char)) {
        weight += 2;
      } else if (gsm7Basic.includes(char)) {
        weight += 1;
      } else {
        isUnicode = true;
        break;
      }
    }

    if (isUnicode) {
      const rawLen = text.length;
      const segs = rawLen <= 70 ? 1 : Math.ceil(rawLen / 67);
      return { count: rawLen, segments: segs, enc: 'unicode' };
    } else {
      const segs = weight <= 160 ? 1 : Math.ceil(weight / 153);
      return { count: weight, segments: segs, enc: 'gsm7' };
    }
  }, []);

  // Fetch settings & balance
  const fetchSmsSettings = useCallback(async () => {
    if (!selectedEvent) return;
    try {
      const res = await api.get(`events/${selectedEvent}/sms/settings`);
      const result = res.data;
      setBalance(result.data.sms_balance);
      setSenderId(result.data.sms_sender_id);
      setTempSenderId(result.data.sms_sender_id);
      setOrganizationName(result.data.organization_name || '');
    } catch (e) {
      console.error('Failed to fetch SMS settings:', e);
    }
  }, [selectedEvent]);

  // Fetch attendees list
  const fetchAttendees = useCallback(async () => {
    if (!selectedEvent) return;
    try {
      const res = await attendeeAPI.getAll(selectedEvent, {});
      const list = res.data?.attendees || res.data || [];
      setAttendees(list);
      
      // Default select all attendees on load
      setSelectedRecipientIds(list.map(a => a.id));
    } catch (e) {
      console.error('Failed to load attendees:', e);
    }
  }, [selectedEvent]);

  // Fetch credit packages
  const fetchPackages = useCallback(async () => {
    try {
      const res = await api.get('sms/packages');
      setPackages(res.data.data?.packages || {});
    } catch (e) {
      console.error('Failed to load packages:', e);
    }
  }, []);

  // Fetch payment records
  const fetchPaymentHistory = useCallback(async () => {
    try {
      const res = await api.get('sms/payments', {
        params: selectedEvent ? { event_id: selectedEvent } : {}
      });
      setPaymentHistory(res.data.data?.data || res.data.data || []);
    } catch (e) {
      console.error('Failed to load payments:', e);
    }
  }, [selectedEvent]);

  // Fetch send logs
  const fetchSendLogs = useCallback(async () => {
    try {
      const res = await api.get('sms/send-logs', {
        params: selectedEvent ? { event_id: selectedEvent } : {}
      });
      setSendLogs(res.data.data?.data || res.data.data || []);
      setSelectedLogs([]); // Clear selection when refreshing logs
    } catch (e) {
      console.error('Failed to load send logs:', e);
    }
  }, [selectedEvent]);

  const toggleLogSelection = (id: string) => {
    setSelectedLogs(prev => 
      prev.includes(id) ? prev.filter(logId => logId !== id) : [...prev, id]
    );
  };

  const toggleSelectAllLogs = () => {
    if (selectedLogs.length === sendLogs.length) {
      setSelectedLogs([]);
    } else {
      setSelectedLogs(sendLogs.map(log => log.id));
    }
  };

  const handleDeleteLogs = async () => {
    if (selectedLogs.length === 0) return;
    try {
      await api.delete('sms/send-logs', { data: { ids: selectedLogs } });
      toast.success(`Successfully deleted ${selectedLogs.length} log(s)`);
      fetchSendLogs();
    } catch (e) {
      console.error('Failed to delete logs:', e);
      toast.error('Failed to delete logs');
    }
  };

  // Initialize and load everything
  useEffect(() => {
    const init = async () => {
      if (!selectedEvent) return;
      setIsLoadingPage(true);
      await Promise.all([
        fetchSmsSettings(),
        fetchAttendees(),
        fetchPackages(),
        fetchPaymentHistory(),
        fetchSendLogs()
      ]);
      setIsLoadingPage(false);
    };
    init();
  }, [selectedEvent, fetchSmsSettings, fetchAttendees, fetchPackages, fetchPaymentHistory, fetchSendLogs]);


  // Update counters when message text changes
  useEffect(() => {
    const calc = calculateSmsParts(message);
    setCharCount(calc.count);
    setSegmentCount(calc.segments);
    setEncoding(calc.enc);
  }, [message, calculateSmsParts]);

  // Calculate total credit cost
  const totalCost = useMemo(() => {
    return segmentCount * selectedRecipientIds.length;
  }, [segmentCount, selectedRecipientIds]);

  // Effective count shown in summary
  const effectiveCount = useMemo(() => {
    return selectedRecipientIds.length;
  }, [selectedRecipientIds]);

  // Update Sender ID
  const handleSaveSenderId = async () => {
    if (!tempSenderId.match(/^[a-zA-Z0-9]+$/) || tempSenderId.length > 11) {
      toast.error('Sender ID must be alphanumeric and up to 11 characters.');
      return;
    }
    setIsSavingSender(true);
    try {
      await api.post(`events/${selectedEvent}/sms/settings`, { sender_id: tempSenderId });
      toast.success('Sender ID updated successfully.');
      fetchSmsSettings();
    } catch (e) {
      toast.error('Error updating Sender ID.');
    } finally {
      setIsSavingSender(false);
    }
  };

  // Buy credits flow with Paystack integration
  const handleBuyCredits = async (units) => {
    setIsInitiatingPayment(true);
    try {
      await loadPaystack();
      
      const res = await api.post('sms/payments/initiate', { 
        units,
        event_id: selectedEvent || undefined
      });
      const paymentData = res.data.data;

      // Launch Paystack Pop
      const handler = (window as any).PaystackPop.setup({
        key: paymentData.paystack_public_key,
        email: paymentData.email,
        amount: Math.round(paymentData.amount * 100), // pesewas conversion
        currency: 'GHS',
        ref: paymentData.reference,
        callback: function(response) {
          toast.success("Payment completed! Waiting for verification...");
          pollPaymentStatus(paymentData.reference);
        },
        onClose: function() {
          toast.info("Transaction closed.");
        }
      });
      handler.openIframe();
    } catch (e) {
      console.error(e);
      toast.error('Payment gateway setup failed.');
    } finally {
      setIsInitiatingPayment(false);
    }
  };

  // Polling verification status
  const pollPaymentStatus = (reference) => {
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      if (attempts > 15) {
        clearInterval(interval);
        toast.error("Verification timeout. Refresh to check status.");
        return;
      }
      try {
        const res = await api.get(`sms/payments/verify/${reference}`);
        const result = res.data;
        if (result.status === 'verified') {
          clearInterval(interval);
          toast.success("Verification successful! Credits added.");
          fetchSmsSettings();
          fetchPaymentHistory();
        } else if (result.status === 'failed') {
          clearInterval(interval);
          toast.error("Payment verification failed on server.");
        }
      } catch (e) {
        console.error(e);
      }
    }, 3000);
  };

  // Send SMS Campaign
  const handleSendCampaign = async () => {
    if (!message.trim()) {
      toast.error('Please type a message.');
      return;
    }
    if (selectedRecipientIds.length === 0) {
      toast.error('Please select at least one recipient.');
      return;
    }
      
    const requestedCost = selectedRecipientIds.length * segmentCount;
    
    // Check if they have enough balance for everyone they want to send to
    let actualCount = selectedRecipientIds.length;
    let willTruncate = false;
    
    if (balance < requestedCost) {
      // Calculate how many people they can actually afford
      actualCount = Math.floor(balance / segmentCount);
      
      if (actualCount === 0) {
        toast.error('Insufficient credits to send even a single message. Please top up your balance.');
        return;
      }
      willTruncate = true;
    }

    // Show custom confirm modal instead of browser confirm
    setPendingSendCount(actualCount);
    setPendingSendCost(actualCount * segmentCount);
    setIsTruncated(willTruncate);
    setShowSendConfirm(true);
  };

  const [activeProgressId, setActiveProgressId] = useState<number | null>(null);
  const [progressData, setProgressData] = useState<any>(null);

  // Poll progress
  useEffect(() => {
    if (!activeProgressId) return;

    let consecutiveErrors = 0;

    const interval = setInterval(async () => {
      try {
        const res = await api.get(`campaigns/progress/${activeProgressId}`);
        const data = res.data?.data || res.data;
        if (!data) return;

        consecutiveErrors = 0; // Reset error count on successful response
        setProgressData(data);

        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(interval);
          setActiveProgressId(null);
          setIsSending(false);
          
          if (data.status === 'completed') {
            toast.success('SMS campaign complete!');
            setSendResult({
               recipient_count: data.total_recipients || data.total_count,
               total_count: data.total_recipients || data.total_count,
               success_count: data.success_count,
               failure_count: data.failure_count,
               status: data.status,
               errors: data.errors || {}
            });
            // Clear message input on success
            setMessage('');
            
            // Refresh balance and logs
            fetchSmsSettings();
            fetchSendLogs();
          } else {
            toast.error('SMS campaign failed to process completely.');
            setSendResult({
               status: 'failed',
               errors: data.errors || {}
            });
          }
        }
      } catch (err: any) {
        consecutiveErrors++;
        // If forbidden (403), unauthenticated (401), or 5 consecutive errors, stop polling gracefully
        if (err?.response?.status === 403 || err?.response?.status === 401 || consecutiveErrors >= 5) {
          clearInterval(interval);
          setActiveProgressId(null);
          setIsSending(false);
          fetchSmsSettings();
          fetchSendLogs();

          if (err?.response?.status === 403) {
            console.warn('Progress check unauthorized (403), stopped polling.');
          } else {
            console.warn('Stopped polling progress after consecutive failures.');
          }
        } else {
          console.warn('Failed to fetch progress (retry ' + consecutiveErrors + '/5):', err?.message || err);
        }
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [activeProgressId, selectedEvent, fetchSmsSettings, fetchSendLogs]);

  const confirmSendCampaign = async () => {
    setShowSendConfirm(false);
    setIsSending(true);
    setSendResult(null);
    setProgressData(null);

    // Slice the recipients to only include the affordable range
    const finalRecipientIds = selectedRecipientIds.slice(0, pendingSendCount);

    try {
      const res = await api.post(`events/${selectedEvent}/sms/send`, {
        recipient_ids: finalRecipientIds,
        message: message,
        personalise: personalise,
        custom_count: pendingSendCount
      });

      const campaignData = res.data?.data;
      if (res.data.success && campaignData) {
        setIsSending(false);
        setActiveProgressId(null);
        toast.success(`SMS campaign complete! (${campaignData.success_count ?? finalRecipientIds.length} sent)`);
        setSendResult({
          recipient_count: campaignData.total_recipients || finalRecipientIds.length,
          total_count: campaignData.total_recipients || finalRecipientIds.length,
          success_count: campaignData.success_count || 0,
          failure_count: campaignData.failure_count || 0,
          status: campaignData.status || 'completed',
          errors: campaignData.errors || {}
        });
        setMessage('');
        fetchSmsSettings();
        fetchSendLogs();
      } else if (res.data.progress_id) {
        setActiveProgressId(res.data.progress_id);
        toast.info('Campaign queued. Tracking progress...');
      } else {
        setIsSending(false);
        toast.error('Failed to dispatch campaign.');
      }
    } catch (e: any) {
      setIsSending(false);
      toast.error(e.response?.data?.message || 'An error occurred during SMS campaign dispatch.');
    }
  };

  const insertPlaceholder = (ph: string) => {
    if (textareaRef.current) {
      const start = textareaRef.current.selectionStart;
      const end = textareaRef.current.selectionEnd;
      const text = message;
      const nextText = text.substring(0, start) + ph + text.substring(end);
      setMessage(nextText);
      setPersonalise(true);
      
      // Reset cursor position
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.selectionStart = textareaRef.current.selectionEnd = start + ph.length;
        }
      }, 0);
    }
  };

  if (!selectedEvent) {
    return (
      <div className="p-6 text-center">
        <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No event selected</h3>
        <p className="mt-1 text-sm text-gray-500">Please select an event to manage SMS campaigns.</p>
      </div>
    );
  }

  if (isLoadingPage) {
    return (
      <div className="h-full flex items-center justify-center p-12">
        <Loading />
      </div>
    );
  }

  return (
    <>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        
        {/* Header Block with Balance */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">SMS Campaigns</h1>
            <p className="text-gray-600 text-sm">Send segmented SMS notifications to event attendees.</p>
            {organizationName && (
              <div className="mt-1 text-xs text-gray-500 flex items-center gap-1.5 font-medium">
                <span className="w-2 h-2 rounded-full bg-teal-500"></span>
                Event Organization: <span className="font-semibold text-gray-800">{organizationName}</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-4 bg-teal-50 border border-teal-200 rounded-lg p-4">
            <div className="bg-teal-600 p-2.5 rounded-md text-white">
              <DollarSign className="w-6 h-6" />
            </div>
            <div>
              <div className="text-xs font-semibold text-teal-800 uppercase tracking-wider">
                {organizationName ? `${organizationName} Balance` : 'Credits Balance'}
              </div>
              <div className="text-3xl font-extrabold text-teal-700">{balance} <span className="text-sm font-normal text-teal-600">units</span></div>
            </div>
          </div>
        </div>

        {/* Tab Controls */}
        <div className="flex overflow-x-auto whitespace-nowrap border-b border-gray-200 bg-white px-2 rounded-t-lg border-t border-x">
          <button
            onClick={() => setActiveTab('compose')}
            className={`py-3 px-6 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'compose'
                ? 'border-teal-600 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Compose campaign
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-3 px-6 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'history'
                ? 'border-teal-600 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Campaign logs
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            className={`py-3 px-6 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'payments'
                ? 'border-teal-600 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Billing history & Buy credits
          </button>
        </div>

        {/* Tab Contents */}
        <div className="bg-white border-x border-b rounded-b-lg p-6 shadow-sm">
          
          {/* TAB 1: COMPOSE */}
          {activeTab === 'compose' && (
            <div className="flex flex-col xl:flex-row gap-6">
              
              {/* Compose fields */}
              <div className="xl:flex-1 space-y-4">
                {/* Sender ID Customization */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-1.5">
                    <Settings className="w-4 h-4 text-gray-500" />
                    SMS Settings
                  </h3>
                  <div className="flex gap-2 max-w-md">
                    <div className="flex-1">
                      <label htmlFor="sender-id-input" className="sr-only">Sender ID</label>
                      <input
                        id="sender-id-input"
                        type="text"
                        placeholder="Sender ID (e.g. InfoSMS)"
                        value={tempSenderId}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none bg-gray-100 text-gray-600 cursor-not-allowed"
                        maxLength={11}
                      />
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 mt-1 block">Outgoing SMS will use this Sender ID.</span>
                </div>

                {/* Message Body */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="block text-sm font-semibold text-gray-700">Message Content</label>
                    
                    {/* Placeholder Chips */}
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => insertPlaceholder('{name}')}
                        className="px-2 py-1 text-xs bg-teal-100 hover:bg-teal-200 text-teal-700 font-semibold rounded transition-colors"
                        title="Inserts attendee first name"
                      >
                        + First Name ({'{name}'})
                      </button>
                    </div>
                  </div>

                  <textarea
                    ref={textareaRef}
                    rows={6}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Type your message here..."
                  />

                  {/* Character counters & segment calculators */}
                  <div className="flex flex-wrap justify-between text-xs text-gray-500 bg-gray-50 p-2.5 rounded border">
                    <div>
                      Encoding: <strong className="uppercase font-bold text-gray-700">{encoding}</strong>
                    </div>
                    <div>
                      Characters: <strong className="font-semibold text-gray-700">{charCount}</strong>
                    </div>
                    <div>
                      Segments: <strong className="font-semibold text-teal-600">{segmentCount}</strong>
                    </div>
                    <div>
                      Est. cost per recipient: <strong className="font-semibold text-teal-600">{segmentCount} credit(s)</strong>
                    </div>
                  </div>
                </div>

                {/* Personalisation Toggle */}
                <div className="flex items-center gap-2 py-1">
                  <input
                    type="checkbox"
                    id="personalise-toggle"
                    checked={personalise}
                    onChange={(e) => setPersonalise(e.target.checked)}
                    className="rounded border-gray-300 text-teal-600 focus:ring-teal-500 h-4 w-4"
                  />
                  <label htmlFor="personalise-toggle" className="text-sm text-gray-700 font-medium cursor-pointer select-none">
                    Personalise messages (replaces <code className="bg-gray-100 px-1 py-0.5 rounded text-xs text-teal-700 font-mono">{'{name}'}</code> with recipient's first name)
                  </label>
                </div>

                {/* Send action triggers */}
                <div className="pt-2">
                  <div className="bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-teal-900">Summary Cost Details</div>
                      <div className="text-xs text-teal-700 mt-0.5">
                        Sending to <strong className="text-teal-900">{effectiveCount}</strong> recipient{effectiveCount !== 1 ? 's' : ''} &mdash; deducting <strong className="text-teal-900">{totalCost}</strong> credit{totalCost !== 1 ? 's' : ''} from your balance.
                      </div>
                    </div>

                    <button
                      onClick={handleSendCampaign}
                      disabled={isSending || selectedRecipientIds.length === 0 || message.trim() === '' || balance < segmentCount}
                      className="w-full sm:w-auto flex-shrink-0 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 active:scale-[0.98] shadow-md hover:shadow-lg transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                    >
                      {isSending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Sending Campaign...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          <span>Send SMS Campaign</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Progress Bar UI */}
                {activeProgressId && progressData && (
                  <div className="border border-teal-200 bg-teal-50 rounded-lg p-4 space-y-3 shadow-sm">
                    <div className="flex justify-between text-sm font-medium text-teal-800">
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Processing Campaign in Background...
                      </span>
                      <span>{Math.round((progressData.processed_count / (progressData.total_recipients || progressData.total_count || 1)) * 100) || 0}%</span>
                    </div>
                    <div className="w-full bg-teal-200 rounded-full h-2.5 overflow-hidden">
                      <div 
                        className="bg-teal-600 h-2.5 rounded-full transition-all duration-500 ease-out" 
                        style={{ width: `${Math.round((progressData.processed_count / (progressData.total_recipients || progressData.total_count || 1)) * 100) || 0}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-teal-700 font-medium">
                      <span>Processed: {progressData.processed_count} / {progressData.total_recipients || progressData.total_count || 0}</span>
                      <span>Success: {progressData.success_count} | Failed: <span className={progressData.failure_count > 0 ? "text-red-600 font-bold" : ""}>{progressData.failure_count}</span></span>
                    </div>
                  </div>
                )}

                {/* Post-send results summary */}
                {sendResult && !activeProgressId && (
                  <div className="border border-green-200 bg-green-50 rounded-lg p-4 space-y-2">
                    <h4 className="text-sm font-bold text-green-800 flex items-center gap-1.5">
                      <Check className="w-5 h-5" />
                      Campaign Complete
                    </h4>
                    <div className="grid grid-cols-3 gap-2 text-xs text-green-700 font-medium">
                      <div>Total Count: <strong>{sendResult.recipient_count || sendResult.total_count || 0}</strong></div>
                      <div>Successful: <strong>{sendResult.success_count || 0}</strong></div>
                      <div className="text-red-600">Failed: <strong>{sendResult.failure_count || 0}</strong></div>
                    </div>
                    {sendResult.errors && typeof sendResult.errors === 'object' && Object.keys(sendResult.errors).length > 0 && (
                      <div className="mt-2 text-xs text-red-700 border-t border-green-200 pt-2 space-y-1">
                        <div className="font-bold">Errors detail:</div>
                        <ul className="list-disc pl-4 space-y-1">
                          {Object.entries(sendResult.errors).slice(0, 5).map(([name, err]) => (
                            <li key={name}><strong>{name}</strong>: {String(err)}</li>
                          ))}
                          {Object.keys(sendResult.errors).length > 5 && (
                            <li>...and {Object.keys(sendResult.errors).length - 5} more errors.</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

              </div>

              {/* Recipient list sidebar checklist */}
              <div className="xl:w-96 shrink-0 border-t xl:border-t-0 xl:border-l border-gray-200 pt-6 xl:pt-0 xl:pl-6">
                <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-1">
                  <Users className="w-4 h-4 text-gray-500" />
                  Campaign Recipients
                </h3>
                <RecipientSelector
                  attendees={attendees}
                  selectedIds={selectedRecipientIds}
                  onChange={setSelectedRecipientIds}
                />
              </div>

            </div>
          )}

          {/* TAB 2: SENT HISTORY LOGS */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <History className="w-5 h-5 text-gray-500" />
                  Outgoing SMS Send Logs
                </h3>
                {selectedLogs.length > 0 && (
                  <button 
                    onClick={handleDeleteLogs}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg shadow-sm transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Selected ({selectedLogs.length})
                  </button>
                )}
              </div>

              {sendLogs.length === 0 ? (
                <div className="text-center py-12 border border-dashed rounded-lg text-gray-500 text-sm">
                  No SMS campaigns sent yet.
                </div>
              ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm text-left text-gray-500 divide-y divide-gray-200">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 w-10">
                          <input 
                            type="checkbox" 
                            className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                            checked={selectedLogs.length === sendLogs.length && sendLogs.length > 0}
                            onChange={toggleSelectAllLogs}
                          />
                        </th>
                        <th className="px-6 py-3">Date & Time</th>
                        <th className="px-6 py-3">Channel</th>
                        <th className="px-6 py-3">Total Recipients</th>
                        <th className="px-6 py-3">Succeeded</th>
                        <th className="px-6 py-3">Failed</th>
                        <th className="px-6 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {sendLogs.map(log => (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input 
                              type="checkbox" 
                              className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                              checked={selectedLogs.includes(log.id)}
                              onChange={() => toggleLogSelection(log.id)}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-900 font-medium">
                            {new Date(log.created_at).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap capitalize">
                            {log.channel}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap font-bold">
                            {log.recipient_count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-green-600 font-bold">
                            {log.success_count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-red-600 font-bold">
                            {log.failure_count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                              Number(log.failure_count) === 0 
                                ? 'bg-green-100 text-green-800' 
                                : Number(log.success_count) === 0 
                                ? 'bg-red-100 text-red-800' 
                                : 'bg-amber-100 text-amber-800'
                            }`}>
                              {Number(log.failure_count) === 0 
                                ? 'Successful' 
                                : Number(log.success_count) === 0 
                                ? 'Failed' 
                                : 'Partial Success'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* TAB 3: BILLING & PACKAGES */}
          {activeTab === 'payments' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              
              {/* Buy credits packages */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Plus className="w-5 h-5 text-teal-600" />
                    Top Up SMS Credits
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">Choose one of our credit packages to top up your balance. Payments are processed securely via Paystack.</p>
                </div>

                <div className="space-y-4">
                  {Object.entries(packages).map(([units, price]) => (
                    <div 
                      key={units}
                      className="group border border-gray-200 rounded-2xl p-5 bg-white hover:border-teal-500 hover:shadow-md cursor-pointer transition-all duration-200 flex justify-between items-center relative overflow-hidden"
                      onClick={() => handleBuyCredits(units)}
                    >
                      <div className="absolute inset-0 bg-teal-50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                      <div className="relative z-10">
                        <div className="text-lg font-bold text-gray-900">{units} <span className="text-sm font-medium text-gray-500">units</span></div>
                        <div className="text-sm text-gray-500 mt-0.5">{(Number(price)/Number(units)).toFixed(4)} GHS / unit</div>
                      </div>
                      <button
                        disabled={isInitiatingPayment}
                        className="relative z-10 px-5 py-2.5 bg-teal-600 text-white font-semibold rounded-xl text-sm hover:bg-teal-700 hover:shadow-lg hover:-translate-y-0.5 transition-all flex flex-col items-center leading-tight disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:shadow-none"
                      >
                        <span>GH₵{(Number(price) * 1.02).toFixed(2)}</span>
                        <span className="text-[10px] font-medium opacity-90 tracking-wide">+2% fee</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Billing transaction history */}
              <div className="lg:col-span-2 space-y-6 lg:border-l border-gray-200 lg:pl-8">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <History className="w-5 h-5 text-gray-500" />
                  Top Up History
                </h3>

                {paymentHistory.length === 0 ? (
                  <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-2xl text-gray-500 text-sm bg-gray-50/50">
                    <History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    No payment attempts recorded.
                  </div>
                ) : (
                  <div className="overflow-x-auto border border-gray-200 rounded-2xl shadow-sm bg-white">
                    <table className="w-full text-sm text-left text-gray-600 divide-y divide-gray-200">
                      <thead className="text-xs text-gray-500 uppercase bg-gray-50/80 font-semibold tracking-wider">
                        <tr>
                          <th className="px-6 py-4 whitespace-nowrap">Date</th>
                          <th className="px-6 py-4 whitespace-nowrap">Reference</th>
                          <th className="px-6 py-4 whitespace-nowrap text-right">Amount</th>
                          <th className="px-6 py-4 whitespace-nowrap text-right">Credits</th>
                          <th className="px-6 py-4 whitespace-nowrap text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {paymentHistory.map(pay => (
                          <tr key={pay.id} className="hover:bg-gray-50/80 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                              {new Date(pay.created_at).toLocaleString(undefined, {
                                year: 'numeric', month: 'short', day: 'numeric',
                                hour: '2-digit', minute: '2-digit'
                              })}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap font-mono text-xs text-gray-500">
                              {pay.paystack_reference}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-gray-900">
                              GH₵{parseFloat(pay.amount).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-teal-600">
                              +{pay.credit_units}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold tracking-wide ${
                                pay.status === 'verified'
                                  ? 'bg-green-100 text-green-700'
                                  : pay.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {pay.status.charAt(0).toUpperCase() + pay.status.slice(1)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

            </div>
          )}

        </div>
      </div>

    {/* SMS Send Confirmation Modal */}
    <ConfirmModal
      isOpen={showSendConfirm}
      onClose={() => setShowSendConfirm(false)}
      onConfirm={confirmSendCampaign}
      title="Send SMS Campaign"
      message={
        isTruncated 
          ? `WARNING: You only have ${balance} credits left, which isn't enough for everyone. This message will only be sent to the first ${pendingSendCount} recipient${pendingSendCount !== 1 ? 's' : ''}, deducting ${pendingSendCost} credit${pendingSendCost !== 1 ? 's' : ''}. Do you want to proceed?`
          : `You're about to send this message to ${pendingSendCount} recipient${pendingSendCount !== 1 ? 's' : ''}. This will deduct up to ${pendingSendCost} credit${pendingSendCost !== 1 ? 's' : ''} from your balance. This action cannot be undone.`
      }
      confirmText="Yes, Send Now"
      cancelText="Go Back"
      variant="warning"
    />
    </>
  );
};

export default SmsCampaigns;
